"use client";

import { motion } from "framer-motion";
import Link from "@/components/LocalizedLink";
import type { ReactElement } from "react";

interface CaseStudy {
  slug: string;
  partner: string;
  title: string;
  summary: string;
  tag: string;
  year: string;
  metrics: { label: string; value: string }[];
}

const caseStudies: CaseStudy[] = [
  {
    slug: "team-telecom-armenia-400g-dwdm",
    partner: "TEAM Telecom Armenia",
    title: "400G DWDM from Armenia to Frankfurt",
    summary:
      "A 400G coherent wavelength in production from Agarak on the Armenian border through to Frankfurt — routed over the Caucasus Cable System submarine link to keep traffic clear of Türkiye terrestrial transit.",
    tag: "Wholesale · 400G DWDM · Submarine · Bypass",
    year: "2026",
    metrics: [
      { label: "Capacity", value: "400G" },
      { label: "Route", value: "AM → FRA" },
      { label: "Submarine", value: "CCS" },
    ],
  },
  {
    slug: "zayo-europe-east-west-connectivity",
    partner: "Zayo Europe",
    title: "Expanding East–West Connectivity with Zayo Europe",
    summary:
      "How Sofia Connect partnered with Zayo Europe to scale capacity, deliver low-latency routes into Western Europe's digital hubs, and reinforce a high-availability Tier 1 backbone for Eastern European customers.",
    tag: "Wholesale · DWDM · IP Backbone",
    year: "2025",
    metrics: [
      { label: "Backbone", value: "Tier 1" },
      { label: "Redundancy", value: "Multi-link" },
      { label: "Reach", value: "East ↔ West EU" },
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

export function CaseStudiesIndexClient(): ReactElement {
  return (
    <main className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 network-grid opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-eyebrow">
              Case Studies
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
              Partnership Stories
            </h1>
            <p className="mt-4 max-w-xl mx-auto text-muted-foreground">
              How Sofia Connect works alongside global carriers and customers to
              deliver resilient, high-capacity connectivity across Southeast
              Europe and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-6">
          {caseStudies.map((cs, i) => (
            <motion.div
              key={cs.slug}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
            >
              <Link
                href={`/case-studies/${cs.slug}`}
                className="bento-card group block h-full transition-all hover:border-accent/40"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-accent/10 text-eyebrow">
                    {cs.partner}
                  </span>
                  <span className="text-xs text-muted font-mono">{cs.year}</span>
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-3 group-hover:text-accent transition-colors">
                  {cs.title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  {cs.summary}
                </p>
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
                  {cs.metrics.map((m) => (
                    <div key={m.label} className="text-center">
                      <p className="text-sm font-bold text-eyebrow font-mono">
                        {m.value}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-xs text-muted">{cs.tag}</p>
                <p className="mt-3 text-sm font-medium text-accent group-hover:underline">
                  Read the story →
                </p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Build your own success story
          </h2>
          <p className="text-muted-foreground mb-8">
            Talk to our team about capacity, routing, and wholesale partnerships
            across the Balkans, Caucasus, and beyond.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-accent text-on-accent rounded-xl hover:bg-accent/90 transition-colors glow-teal"
          >
            Contact Sales
          </Link>
        </div>
      </section>
    </main>
  );
}
